// Curriculum geometry: reusable glyphs + pre-writing strokes + a composer that
// turns any activity into the same "traceable" shape the existing tracing engine
// already understands ({ viewBox, strokes:[{d}], startingPoint, guidelines }).
//
// Metric (0..100 box): ascender top 16, x-height top 46, baseline 78, descender 95.

import { getCursiveFont, glyphPathsForText } from "@/lib/cursiveFont";

export const GUIDES = { top: 16, mid: 46, base: 78, bottom: 95 };
const BL = 78, XT = 46, AT = 16, DB = 95;
export const ADV = 44; // per-glyph advance width (lowercase)
export const ADV_UP = 56; // per-glyph advance width (uppercase, wider capitals)
const PAD = 8;

// Shift every x by dx and y by dy. Works because all commands use x,y pairs.
export function translatePath(d, dx, dy = 0) {
  let i = 0;
  return d.replace(/-?\d*\.?\d+/g, (n) => {
    const v = parseFloat(n) + (i % 2 === 0 ? dx : dy);
    i++;
    return (Math.round(v * 100) / 100).toString();
  });
}

// --- Lowercase cursive glyphs (single-box, baseline metric) ---
// American cursive (Zaner-Bloser style). Every letter starts with an
// undercurve entry from the left and ends with an exit stroke on the right
// so consecutive letters can connect. Ascenders (b d f h k l t) loop.
// Descenders (f g j p q y z) loop under the baseline.
// Each: { d, start:{x,y}, adv }.
const g = (d, sx, sy, adv = ADV) => ({ d, start: { x: sx, y: sy }, adv });

export const GLYPHS = {
  // a — oval + right stem-down + exit undercurve
  a: g("M 38 50 C 32 44 22 44 16 50 C 10 58 10 70 18 74 C 26 78 36 74 38 66 C 38 62 38 56 38 50 L 38 76 C 40 80 46 80 48 74", 38, 50),
  // b — tall loop entering on LEFT rail, then belly with retrace + exit
  b: g("M 10 74 C 14 58 22 36 22 22 C 22 16 30 16 28 26 C 24 42 20 60 22 74 C 24 80 34 82 40 74 C 44 68 40 60 32 60 C 26 60 24 66 30 66 C 36 66 42 66 46 62", 22, 22),
  // c — simple curve
  c: g("M 42 54 C 38 46 26 44 18 50 C 10 56 10 70 18 76 C 26 80 38 78 44 70", 42, 54),
  // d — oval belly + tall right-side loop stem + exit
  d: g("M 38 52 C 32 46 22 46 16 52 C 10 60 10 72 18 76 C 26 80 38 76 38 66 M 34 22 C 34 16 42 16 38 26 C 34 42 30 62 34 74 C 38 80 46 80 48 74", 38, 52),
  // e — small loop then round exit
  e: g("M 14 62 C 22 60 32 60 38 62 C 34 52 24 46 16 52 C 10 58 10 70 18 76 C 26 80 38 78 44 70", 14, 62),
  // f — tall loop up + straight body + descender loop + tiny cross
  f: g("M 24 84 C 26 60 32 30 32 20 C 32 14 38 14 36 24 C 32 48 26 74 20 90 C 16 94 10 92 14 86 C 16 82 22 82 22 88 M 14 50 C 22 48 30 48 36 50", 32, 20),
  // g — oval + straight descender loop
  g: g("M 38 52 C 32 46 22 46 16 52 C 10 60 10 72 18 76 C 26 80 38 76 38 66 L 38 84 C 36 92 26 96 18 90 C 14 86 22 86 24 90", 38, 52),
  // h — tall LEFT-rail loop then hump + exit
  h: g("M 10 74 C 14 58 22 36 22 22 C 22 16 30 16 28 26 C 24 42 20 60 22 74 C 24 60 30 54 34 54 C 40 54 40 62 36 74 C 38 80 46 80 48 74", 22, 22),
  // i — small u + dot
  i: g("M 12 62 C 14 56 20 52 22 50 C 22 60 20 70 22 74 C 24 80 32 80 34 74 M 22 38 L 24 40", 22, 50),
  // j — descender loop + dot
  j: g("M 12 62 C 14 56 22 52 24 50 C 24 64 22 78 20 86 C 18 92 12 92 14 86 C 16 82 22 82 22 88 M 24 38 L 26 40", 24, 50),
  // k — tall LEFT-rail loop then little belly + exit
  k: g("M 10 74 C 14 58 22 36 22 22 C 22 16 30 16 28 26 C 24 42 20 60 22 74 M 40 54 C 34 60 26 62 22 66 C 26 66 32 70 32 74 C 30 78 38 80 42 74", 22, 22),
  // l — tall LEFT-rail loop, straight down, exit
  l: g("M 14 74 C 18 58 24 34 26 22 C 26 16 32 16 30 26 C 26 42 22 60 22 74 C 24 80 34 80 40 72", 26, 22),
  // m — three humps + exit
  m: g("M 8 74 C 10 66 14 56 16 50 C 16 60 16 68 16 74 C 18 62 22 54 28 54 C 32 54 32 62 30 74 C 32 62 36 54 42 54 C 46 54 46 62 44 74 C 46 80 54 80 56 74", 16, 50),
  // n — two humps + exit
  n: g("M 8 74 C 10 66 14 56 16 50 C 16 60 16 68 16 74 C 18 62 22 54 28 54 C 34 54 36 62 34 74 C 36 80 44 80 46 74", 16, 50),
  // o — oval with exit tail at top-right
  o: g("M 38 54 C 32 46 20 46 14 54 C 8 62 10 74 20 76 C 30 78 40 72 40 62 C 40 56 38 52 34 50 C 40 50 46 52 48 56", 38, 54),
  // p — descender straight down + belly on the right
  p: g("M 8 62 C 10 56 14 52 16 50 C 16 66 12 82 10 92 M 16 60 C 22 54 32 52 38 58 C 44 64 42 74 34 76 C 26 78 18 74 14 68", 16, 50),
  // q — oval + straight descender + hook
  q: g("M 38 52 C 32 46 22 46 16 52 C 10 60 10 72 18 76 C 26 80 38 76 38 66 L 38 90 C 40 94 46 94 48 88", 38, 52),
  // r — small entry hump then top-right tick
  r: g("M 8 74 C 10 64 14 56 16 50 C 16 58 16 66 16 74 C 18 62 22 54 30 54 C 34 54 38 58 36 62 C 38 60 42 58 46 60", 16, 50),
  // s — American cursive (matching Zaner-Bloser reference): narrow slanted
  // leaf/teardrop. Undercurve from baseline rises to a peak at x-height, then
  // curves down on the right side of the leaf back toward the baseline,
  // finishing with a small exit tail.
  s: g("M 10 74 C 14 62 20 52 26 46 C 32 52 34 66 24 74 C 30 72 40 68 46 66", 26, 46),
  // t — straight tall stem + cross + exit
  t: g("M 26 20 C 24 40 20 62 20 74 C 22 80 30 80 34 74 M 12 46 C 20 46 30 46 36 46", 26, 20),
  // u — two u-humps + exit
  u: g("M 12 50 C 12 60 10 70 14 74 C 20 78 26 74 30 66 C 30 72 30 74 32 76 C 32 62 34 54 36 50 C 36 60 34 68 36 74 C 38 80 46 80 48 74", 12, 50),
  // v — v-shape + exit
  v: g("M 12 50 C 12 60 16 70 22 74 C 28 68 32 60 36 50 C 38 60 36 68 40 74 C 42 78 48 78 50 74", 12, 50),
  // w — three points + exit
  w: g("M 8 50 C 8 62 12 72 18 74 C 22 70 26 62 28 54 C 30 62 32 72 36 74 C 40 72 44 62 46 54 C 46 62 46 72 48 74 C 50 78 56 78 58 74", 8, 50),
  // x — two crossed diagonals + finishing tick
  x: g("M 10 52 C 20 60 30 70 40 76 M 40 52 C 30 60 20 70 12 76 M 38 74 C 42 72 46 74 46 76", 10, 52),
  // y — u-shape + descender loop
  y: g("M 12 50 C 12 60 10 70 14 74 C 20 78 26 74 30 66 C 30 72 30 74 32 76 C 34 62 36 54 38 50 C 38 60 36 76 32 84 C 28 92 20 94 18 88 C 18 84 26 84 28 88", 12, 50),
  // z — angular z with descender loop
  z: g("M 10 54 C 16 50 26 50 34 54 C 28 62 20 70 14 76 C 22 78 32 78 38 74 M 38 74 C 36 84 30 92 34 92 C 38 94 44 94 46 88", 10, 54),
};

// --- Uppercase cursive glyphs (span ascender-to-baseline, ~0..56 wide) ---
// Traditional American cursive capitals. Each letter has a clear starting
// point (marked in the tutorial player) and uses as few strokes as possible.
// Multi-stroke letters split naturally on `M` boundaries.
const gu = (d, sx, sy, adv = ADV_UP) => ({ d, start: { x: sx, y: sy }, adv });

export const GLYPHS_UP = {
  // A — big oval + straight descender (like a huge lowercase a)
  A: gu("M 44 40 C 40 26 28 22 20 30 C 10 40 10 60 20 70 C 30 78 42 74 44 62 C 44 54 44 48 44 40 L 44 78 C 46 82 52 82 54 76", 44, 40),
  // B — loop-top stem + top belly + bigger bottom belly
  B: gu("M 12 78 C 16 60 22 40 26 24 C 28 18 34 20 32 30 C 28 46 22 66 20 78 C 22 62 30 44 40 34 C 46 30 50 40 44 46 C 38 50 24 50 20 50 C 30 52 44 54 46 64 C 48 74 40 80 30 80 C 24 80 20 78 20 74", 26, 24),
  // C — big open C with small entry hook
  C: gu("M 52 32 C 48 22 34 20 22 28 C 10 38 10 62 22 74 C 34 82 50 78 54 68", 52, 32),
  // D — loop-top stem + wide oval belly
  D: gu("M 12 78 C 16 60 22 40 26 24 C 28 18 34 20 32 30 C 28 46 22 66 20 78 C 20 78 26 72 34 68 C 48 62 54 46 46 32 C 38 22 24 22 20 30", 26, 24),
  // E — script E: small top belly + bigger bottom belly (backward 3)
  E: gu("M 48 30 C 42 22 28 22 24 30 C 20 38 30 44 36 44 C 28 44 20 50 22 60 C 24 72 40 78 52 68", 48, 30),
  // F — cursive F: single tall stem with descender loop + top hat + cross
  F: gu("M 30 78 C 32 60 38 40 40 24 C 40 18 46 20 44 28 C 40 48 34 68 30 84 C 28 92 20 92 22 86 C 24 82 30 82 30 88 M 22 30 C 30 24 42 22 50 26 M 18 50 C 26 48 34 48 40 50", 40, 24),
  // G — oval + horizontal shelf below
  G: gu("M 48 34 C 44 22 30 20 20 28 C 8 38 8 60 22 72 C 34 82 50 76 52 62 C 52 56 46 52 40 52 L 52 52", 48, 34),
  // H — two loop-top stems connected by a swoopy crossbar
  H: gu("M 12 78 C 16 60 22 40 26 24 C 28 18 34 20 32 30 C 28 46 22 66 20 78 M 20 56 C 26 52 34 56 40 60 C 44 62 50 60 54 56 M 54 24 C 54 42 50 62 46 78 C 44 82 52 82 54 76", 26, 24),
  // I — cursive script I: loop top + tall stem
  I: gu("M 20 78 C 26 72 32 60 34 48 C 34 42 30 40 28 42 C 26 46 30 50 34 48 C 38 40 44 30 48 26 C 44 24 40 24 36 26 C 32 40 28 60 26 76 C 24 82 18 82 20 76", 48, 26),
  // J — cursive script J: loop top + descender loop
  J: gu("M 48 26 C 44 24 40 24 36 26 C 32 42 28 60 24 82 C 22 90 14 92 12 86 C 14 82 20 82 22 88", 48, 26),
  // K — loop-top stem + upper curl + lower diagonal
  K: gu("M 12 78 C 16 60 22 40 26 24 C 28 18 34 20 32 30 C 28 46 22 66 20 78 M 48 26 C 40 34 30 44 24 50 C 30 52 38 54 40 62 C 44 68 40 76 34 78 C 30 78 26 74 28 68", 26, 24),
  // L — cursive L: big top loop, straight down, bottom curl exit
  L: gu("M 42 30 C 32 22 20 30 22 44 C 24 34 32 30 34 36 C 30 48 22 66 24 76 C 30 82 46 80 54 68", 42, 30),
  // M — cursive M: entry undercurve + three humps + exit
  M: gu("M 8 78 C 10 66 14 54 18 46 C 20 50 20 56 20 62 C 22 54 26 46 32 44 C 36 44 38 52 36 62 C 38 54 42 46 48 44 C 52 44 54 52 52 62 C 52 54 52 68 52 78 C 54 82 60 82 62 76", 18, 46),
  // N — cursive N: entry undercurve + two humps + exit
  N: gu("M 8 78 C 10 66 14 54 18 46 C 20 50 20 56 20 62 C 22 54 30 46 40 44 C 46 44 48 52 44 62 C 44 54 44 68 44 78 C 46 82 52 82 54 76", 18, 46),
  // O — big oval with entry loop at top
  O: gu("M 48 32 C 42 22 26 22 18 32 C 10 44 12 62 22 72 C 34 82 50 76 52 60 C 54 44 46 30 34 28 C 40 24 46 28 48 32", 48, 32),
  // P — loop-top stem (descending into desc zone) + top belly
  P: gu("M 14 92 C 18 70 24 46 28 26 C 30 20 34 20 32 30 C 28 48 22 68 18 82 M 30 30 C 42 24 52 32 50 44 C 48 56 34 60 22 56", 28, 26),
  // Q — cursive Q looks like a "2"
  Q: gu("M 14 34 C 18 28 24 22 32 22 C 44 22 50 32 44 42 C 38 50 26 62 16 76 C 28 74 42 74 54 78", 14, 34),
  // R — B-style loop-top + top belly + kicking leg
  R: gu("M 12 78 C 16 60 22 40 26 24 C 28 18 34 20 32 30 C 28 46 22 66 20 78 C 22 62 30 44 40 34 C 46 30 50 40 44 46 C 38 50 24 50 20 50 C 28 52 36 58 42 66 C 46 74 46 80 42 82", 26, 24),
  // S — cursive S
  S: gu("M 16 34 C 22 24 32 22 38 26 C 32 26 30 32 34 34 C 42 30 52 32 50 40 C 48 50 34 56 24 60 C 18 62 16 68 20 74 C 26 78 32 74 34 68", 16, 34),
  // T — cursive T: tall loop stem + top crossbar
  T: gu("M 30 78 C 32 60 38 40 40 24 C 40 18 46 20 44 28 C 40 48 36 68 34 78 C 32 84 24 84 26 78 M 18 26 C 30 22 44 22 52 26", 40, 24),
  // U — big U with entry curl + exit stem
  U: gu("M 18 26 C 16 42 12 62 16 72 C 22 80 32 76 36 66 C 40 56 42 44 42 32 C 42 50 42 66 44 78 C 46 82 52 82 54 76", 18, 26),
  // V — V shape + tail
  V: gu("M 14 30 C 14 46 18 62 26 78 C 32 66 40 46 46 26 C 48 22 54 22 54 26", 14, 30),
  // W — W shape + tail
  W: gu("M 10 28 C 10 42 12 60 18 78 C 22 68 26 56 28 44 C 30 56 32 68 36 78 C 40 60 44 42 46 26 C 48 22 54 22 54 26", 10, 28),
  // X — two crossed diagonals + small finish tick
  X: gu("M 14 28 C 24 44 38 62 50 78 M 48 28 C 38 44 24 62 14 78 M 44 76 C 48 74 52 76 52 78", 14, 28),
  // Y — V-shape + descender loop
  Y: gu("M 14 30 C 14 46 18 62 26 68 C 32 62 40 46 46 30 C 48 26 54 26 54 30 C 48 46 40 66 32 82 C 28 92 20 92 18 86 C 20 82 26 82 28 88", 14, 30),
  // Z — cursive Z: "3" shape + descender loop
  Z: gu("M 18 30 C 26 22 42 22 50 30 C 40 42 26 58 20 68 C 30 66 42 66 50 70 M 28 74 C 26 84 20 90 24 92 C 28 94 34 92 32 86", 18, 30),
};

// --- Pre-writing strokes (0..100 box) ---
const s = (d, sx, sy) => ({ d, start: { x: sx, y: sy } });
export const STROKES = {
  curve_up: s("M 15 78 C 40 78 60 60 85 30", 15, 78),
  curve_down: s("M 15 30 C 40 60 60 78 85 78", 15, 30),
  curve_cw: s("M 30 30 C 70 25 80 55 60 75 C 45 88 25 80 25 60", 30, 30),
  curve_ccw: s("M 70 30 C 30 25 20 55 40 75 C 55 88 75 80 75 60", 70, 30),
  oval: s("M 65 30 C 40 22 22 40 25 58 C 28 76 55 82 70 66 C 80 55 78 34 65 30", 65, 30),
  undercurve: s("M 20 40 C 25 70 45 80 55 55 C 63 70 78 72 85 55", 20, 40),
  overcurve: s("M 20 60 C 28 30 48 25 55 50 C 62 30 80 30 85 55", 20, 60),
  loop_up: s("M 30 80 C 42 55 55 30 45 22 C 38 17 32 30 42 45 C 52 60 60 72 62 80", 30, 80),
  loop_down: s("M 40 25 C 45 50 52 72 42 80 C 34 86 28 74 38 60 C 46 48 55 38 60 25", 40, 25),
  loop_tall: s("M 28 80 C 42 50 58 22 46 16 C 38 12 32 26 44 42 C 54 55 60 68 60 80", 28, 80),
  loop_small: s("M 35 70 C 45 55 55 48 48 44 C 43 41 39 48 46 56 C 52 62 58 66 62 70", 35, 70),
  loops_repeat: s("M 15 78 C 24 55 34 40 28 34 C 23 30 20 40 28 52 C 34 62 40 70 44 78 C 52 55 62 40 56 34 C 51 30 48 40 56 52 C 62 62 70 70 82 76", 15, 78),
  wave_shallow: s("M 12 60 C 25 45 35 45 48 60 C 61 75 71 75 84 60", 12, 60),
  wave_tall: s("M 12 70 C 22 35 34 35 46 68 C 58 40 70 40 84 66", 12, 70),
  waves_repeat: s("M 10 62 C 20 48 28 48 38 62 C 48 76 56 76 66 62 C 74 50 82 52 90 62", 10, 62),
  slant_up: s("M 25 80 L 65 25", 25, 80),
  slant_down: s("M 30 25 L 70 80", 30, 25),
  slants_repeat: s("M 20 80 L 40 30 M 45 80 L 65 30 M 70 80 L 90 30", 20, 80),
  entry_stroke: s("M 15 78 C 25 70 35 66 45 66", 15, 78),
  exit_stroke: s("M 45 66 C 55 66 65 70 78 60", 45, 66),
  undercurve_connector: s("M 18 66 C 26 78 40 80 48 66 C 54 78 68 80 78 66", 18, 66),
  overcurve_connector: s("M 18 60 C 26 44 40 44 48 60 C 54 44 68 44 78 60", 18, 60),
  loop_connector: s("M 20 76 C 34 52 46 38 40 34 C 35 31 33 40 40 50 C 48 62 60 70 80 72", 20, 76),
  connectors_repeat: s("M 12 70 C 22 78 34 78 42 68 C 50 60 60 60 68 68 C 74 74 82 74 90 68", 12, 70),
};

const WORD_GAP = 22;
const MIN_WIDTH = 120; // keep single letters from rendering too tall/narrow

// Split a path `d` string on top-level Move (`M`) commands into individual
// sub-strokes. Each returned segment begins with its own `M` — so kids see the
// pen lift + restart on multi-stroke capitals (H, K, F, T, X, Y, Q, I, R).
function splitByM(d) {
  const out = [];
  const re = /M[^M]*/g;
  let m;
  while ((m = re.exec(d)) !== null) out.push(m[0].trim());
  return out.length ? out : [d];
}

// Parse the first "M x y" of a sub-path into a {x, y} start point.
function firstMoveTo(d) {
  const m = /M\s*(-?\d*\.?\d+)\s*[,\s]\s*(-?\d*\.?\d+)/.exec(d);
  return m ? { x: parseFloat(m[1]), y: parseFloat(m[2]) } : { x: 0, y: 0 };
}

// Compose a traceable object for a text of letters (words / connections /
// sentences / single uppercase letters). Uses hand-coded single-line paths
// (Zaner-Bloser American cursive style) so each letter renders as a single
// stroke — no double outline from font glyphs, and the trace pen follows the
// same centerline the student sees.
export function composeText(text, opts = {}) {
  // Font-derived outlines produce a doubled trace (perimeter of a filled
  // glyph), which is impossible to score cleanly and confuses kids. We only
  // fall through to the font branch if a caller explicitly opts in.
  if (opts.useFont === true) {
    const font = getCursiveFont();
    if (font) {
      const fromFont = glyphPathsForText(font, text, { baseline: BL, ascenderTop: AT });
      if (fromFont) {
        return {
          ...fromFont,
          guidelines: GUIDES,
          get fullPath() { return this.strokes.map((x) => x.d).join(" "); },
        };
      }
    }
  }
  let strokes = [];
  let cursor = PAD;
  let start = null;
  for (const ch of text) {
    if (ch === " ") { cursor += WORD_GAP; continue; }
    const isUpper = ch !== ch.toLowerCase() && ch === ch.toUpperCase();
    const glyph = isUpper ? GLYPHS_UP[ch] : GLYPHS[ch.toLowerCase()];
    if (!glyph) { cursor += ADV; continue; }
    // Multi-stroke capitals split into separate strokes so animator / scorer can
    // show a fresh pen start for each part.
    const subs = splitByM(glyph.d);
    for (const sub of subs) {
      const d = translatePath(sub, cursor, 0);
      const startPt = firstMoveTo(d);
      strokes.push({ d, start: startPt });
    }
    if (!start) start = { x: glyph.start.x + cursor, y: glyph.start.y };
    cursor += glyph.adv;
  }
  let width = cursor + PAD;
  // Center narrow content inside a comfortable minimum width.
  if (width < MIN_WIDTH) {
    const dx = (MIN_WIDTH - width) / 2;
    strokes = strokes.map((s) => ({
      d: translatePath(s.d, dx, 0),
      start: s.start ? { x: s.start.x + dx, y: s.start.y } : undefined,
    }));
    if (start) start = { x: start.x + dx, y: start.y };
    width = MIN_WIDTH;
  }
  return {
    viewBox: `0 0 ${width} 100`,
    width,
    strokes: strokes.length ? strokes : [{ d: `M ${PAD} ${BL} L ${width - PAD} ${BL}` }],
    startingPoint: start || { x: PAD, y: BL },
    guidelines: GUIDES,
    glyphText: text,
    get fullPath() { return this.strokes.map((x) => x.d).join(" "); },
  };
}

// Main entry: build a traceable object from a curriculum activity.
export function buildTraceable(activity) {
  if (!activity) return null;
  if (activity.type === "prewriting") {
    const st = STROKES[activity.content];
    if (!st) return null;
    return {
      viewBox: "0 0 100 100",
      width: 100,
      strokes: [{ d: st.d }],
      startingPoint: st.start,
      guidelines: GUIDES,
      get fullPath() { return this.strokes.map((x) => x.d).join(" "); },
    };
  }
  if (["lowercase_letter", "uppercase_letter", "connection", "word", "sentence"].includes(activity.type)) {
    return composeText(activity.content);
  }
  return null; // independent / challenge handled elsewhere
}

// A practice row: the same letter repeated (for the lowercase practice row).
export function composeRow(letter, count = 5) {
  return composeText(new Array(count).fill(letter).join(""));
}
